/**
 * SecureImage
 * -----------
 * Drop-in <img> replacement for HMAC-signed supplier media URLs.
 *
 * When a signed URL expires the browser fires onError (HTTP 403).
 * This component:
 *   1. Detects whether the URL has expired by parsing the `exp` query param.
 *   2. Calls POST /api/supplier/media/refresh-urls with the image_id.
 *   3. Updates src with the fresh signed URL and retries — exactly once.
 *   4. If the retry also fails, shows the fallback element (default: nothing).
 *
 * Usage:
 *   <SecureImage src={img.thumb_url} alt={p.title} className="w-full h-full object-cover" />
 */

import React, { useState, useEffect, useRef } from 'react';
import { refreshMediaUrls } from '../../api/supplierApi';

function extractImageId(src) {
  try {
    const path = new URL(src, window.location.origin).pathname;
    return path.split('/').pop() || null;
  } catch {
    return null;
  }
}

function isThumbUrl(src) {
  try {
    return new URL(src, window.location.origin).searchParams.get('thumb') === '1';
  } catch {
    return false;
  }
}

function isUrlExpired(src) {
  try {
    const exp = parseInt(new URL(src, window.location.origin).searchParams.get('exp') || '0', 10);
    return exp > 0 && Date.now() / 1000 > exp;
  } catch {
    return false;
  }
}

export default function SecureImage({ src, alt = '', className = '', fallback = null, ...rest }) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [failed, setFailed] = useState(false);
  const retried = useRef(false);

  // Reset state when src prop changes (e.g., after new upload)
  useEffect(() => {
    setCurrentSrc(src);
    setFailed(false);
    retried.current = false;
  }, [src]);

  const handleError = () => {
    if (retried.current || !currentSrc) {
      setFailed(true);
      return;
    }
    retried.current = true;

    const imageId = extractImageId(currentSrc);
    if (!imageId) { setFailed(true); return; }

    // Use .then/.catch (not async/await) so React's event system
    // does not intercept the returned Promise and show dev overlay.
    refreshMediaUrls([imageId])
      .then(freshMap => {
        const fresh = freshMap[imageId];
        if (!fresh) { setFailed(true); return; }
        const freshUrl = isThumbUrl(currentSrc) ? fresh.thumb_url : fresh.url;
        setCurrentSrc(freshUrl);
      })
      .catch(() => setFailed(true));
  };

  if (failed) return fallback;
  if (!currentSrc) return fallback;

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      onError={handleError}
      {...rest}
    />
  );
}
